import React, { useState, useEffect, useRef, useContext } from 'react';
import { ThemeContext } from '../context/ThemeContext'; // Import ThemeContext

// This component will use the real API service from your backend

function StreamingReasoningChain({ question, sessionId, requestId, onComplete, onError }) {
  // Guard to ensure onComplete/onError is only called once per stream
  const completedRef = useRef(false);
  // Safely access theme context - it may not be available yet
  let theme = 'light'; // Default theme
  try {
    const themeContext = useContext(ThemeContext);
    if (themeContext) {
      theme = themeContext.theme;
    }
  } catch (error) {
    console.warn('ThemeContext not available:', error);
  }
  
  const [reasoningSteps, setReasoningSteps] = useState([]);
  const [currentStep, setCurrentStep] = useState(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [stepAnimations, setStepAnimations] = useState({});
  const [finalAnswer, setFinalAnswer] = useState(null);
  const [contributingChunks, setContributingChunks] = useState([]);
  const [retrievalId, setRetrievalId] = useState(null);
  const [refinedQuery, setRefinedQuery] = useState('');
  
  // Simplified state for compact interface
  const [showThinking, setShowThinking] = useState(false);
  const [currentStatus, setCurrentStatus] = useState('');
  
  const streamCleanupRef = useRef(null);
  const animationTimeouts = useRef({});
  const isStreamingRef = useRef(false);
  
  const finalAnswerRef = useRef(null);
  const contributingChunksRef = useRef([]);
  const retrievalIdRef = useRef(null);
  const reasoningStepsRef = useRef([]);

  // Cuckoo status messages based on reasoning steps
  const getCuckooStatusMessage = (stepType, stepData) => {
    const messages = {
      start: '🐦 Cuckoo is waking up... ',
      thought: '💭 Cuckoo is pondering deeply... ',
      action: '🧭 Cuckoo is exploring the knowledge base... ',
      observation: '🔎 Cuckoo is uncovering insights... ',
      final_answer: '🏆 Cuckoo has the solution ready! ',
      error: '🚨 Cuckoo ran into a snag... ',
      keepalive: '⚡ Cuckoo is staying active... '
    };
    return messages[stepType] || 'Cuckoo is doing its magic... 🔄';
  };

  // Return empty descriptions to remove headings
  const getStepDescription = (stepType, stepData) => {
    // Return empty string to remove all headings
    return '';
  };

  // Start streaming when component mounts
  useEffect(() => {
    if (!question || !sessionId) return;

    if (isStreamingRef.current) return;

    isStreamingRef.current = true;
    setIsStreaming(true);
    setReasoningSteps([]);
    setCurrentStep(null);
    setStepAnimations({});
    setRefinedQuery('');
    setCurrentStatus('Cuckoo is starting up... 🚀');
    
    setFinalAnswer(null);
    setContributingChunks([]);
    setRetrievalId(null);
    
    finalAnswerRef.current = null;
    contributingChunksRef.current = [];
    retrievalIdRef.current = null;
    reasoningStepsRef.current = [];

    // Import API service dynamically to avoid circular dependencies
    import('../services/api').then(({ default: apiService }) => {
      console.log('StreamingReasoningChain: API service imported, starting stream');
      const cleanup = apiService.streamReasoning(
        question,
        sessionId,
        handleReasoningStep,
        handleStreamComplete,
        handleStreamError
      );
      
      streamCleanupRef.current = cleanup;
    }).catch(error => {
      console.error('Failed to import API service:', error);
      handleStreamError('Failed to start reasoning stream');
    });

    return () => {
      isStreamingRef.current = false;
      Object.values(animationTimeouts.current).forEach(timeout => clearTimeout(timeout));
      animationTimeouts.current = {};
      if (streamCleanupRef.current) {
        streamCleanupRef.current();
      }
    };
  }, [question, sessionId]);

  const handleReasoningStep = (stepData) => {
    // Update current status with Cuckoo messages
    setCurrentStatus(getCuckooStatusMessage(stepData.type, stepData));

    // Extract refined query from action step
    if (stepData.type === 'action' && stepData.content?.details?.tool_input) {
      const originalQuery = stepData.content.details.tool_input;
      if (!refinedQuery) {
        setRefinedQuery(originalQuery);
      }
    }
    
    // Store retrieved filenames and content summaries for observation steps
    if (stepData.type === 'observation') {
      if (stepData.content?.details?.retrieved_filenames) {
        console.log('Retrieved files:', stepData.content.details.retrieved_filenames);
      }
      
      // Check for refined query in observation step
      if (stepData.content?.details?.refined_query) {
        const refinedQueryText = stepData.content.details.refined_query;
        if (refinedQueryText && refinedQueryText !== refinedQuery) {
          setRefinedQuery(refinedQueryText);
        }
      }
      
      // Store contributing chunks if available
      if (stepData.content?.details?.contributing_chunks) {
        setContributingChunks(stepData.content.details.contributing_chunks);
        contributingChunksRef.current = stepData.content.details.contributing_chunks;
      }
    }
    
    // Handle final answer
    if (stepData.type === 'final_answer') {
      if (stepData.content?.details) {
        const details = stepData.content.details;
        
        if (details.answer) {
          setFinalAnswer(details.answer);
          finalAnswerRef.current = details.answer;
        }
        
        if (details.contributing_chunks) {
          setContributingChunks(details.contributing_chunks);
          contributingChunksRef.current = details.contributing_chunks;
        }
        
        if (details.retrieval_id) {
          setRetrievalId(details.retrieval_id);
          retrievalIdRef.current = details.retrieval_id;
        }
      }
    }
    
    setReasoningSteps(prev => {
      const stepKey = `${stepData.step_number}-${stepData.type}`;
      const exists = prev.some(step => `${step.step_number}-${step.type}` === stepKey);
      
      if (exists) {
        return prev;
      }
      
      const newSteps = [...prev, stepData];
      reasoningStepsRef.current = newSteps;
      return newSteps;
    });
    setCurrentStep(stepData);
    
    const stepKey = `${stepData.step_number}-${stepData.type}`;
    
    // Mark all steps as complete immediately (no typing animation)
    setStepAnimations(prev => ({
      ...prev,
      [stepKey]: {
        animatedText: stepData.content?.message || '',
        charIndex: (stepData.content?.message || '').length,
        isComplete: true
      }
    }));
  };

  const handleStreamComplete = () => {
    if (completedRef.current) return;
    completedRef.current = true;
    isStreamingRef.current = false;
    setIsStreaming(false);
    setCurrentStep(null);
    setCurrentStatus('Cuckoo has completed the analysis!');
    
    setTimeout(() => {
      const currentFinalAnswer = finalAnswerRef.current;
      const currentContributingChunks = contributingChunksRef.current;
      const currentRetrievalId = retrievalIdRef.current;
      const currentReasoningSteps = reasoningStepsRef.current;
      if (currentFinalAnswer) {
        onComplete && onComplete(currentFinalAnswer, currentContributingChunks, currentRetrievalId, requestId);
      } else {
        const finalAnswerStep = currentReasoningSteps.find(step => step.type === 'final_answer');
        if (finalAnswerStep && finalAnswerStep.content?.details?.answer) {
          onComplete && onComplete(finalAnswerStep.content.details.answer, currentContributingChunks, currentRetrievalId, requestId);
        } else {
          onComplete && onComplete(null, currentContributingChunks, currentRetrievalId, requestId);
        }
      }
    }, 1000);
  };

  const handleStreamError = (error) => {
    if (completedRef.current) return;
    completedRef.current = true;
    isStreamingRef.current = false;
    setIsStreaming(false);
    setCurrentStatus('⚠️ Error encountered');
    setCurrentStep({
      type: 'error',
      content: {
        message: `Error: ${error}`,
        details: { error }
      }
    });
    onError && onError(error);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    try {
      return new Date(timestamp).toLocaleTimeString();
    } catch {
      return '';
    }
  };

  // Filter out start, action, and final_answer steps
  const displaySteps = reasoningSteps.filter(step => 
    step.type !== 'start' && 
    step.type !== 'action' && 
    step.type !== 'final_answer'
  );

  return (
    <div className="message-container ai-message">
      <div className="processing-avatar-container">
        <div className="message-avatar ai-icon-processing"></div>
      </div>
      
      <div className="reasoning-container-compact">
        {/* Cuckoo Status Header */}
        <div className="reasoning-header-compact">
          <div className="status-display">
            <div className="current-status cuckoo-status">
              <span className="status-text glazed-text">{currentStatus}</span>
            </div>
          </div>
          <button 
            className={`expand-toggle ${showThinking ? 'expanded' : 'collapsed'}`}
            onClick={() => setShowThinking(!showThinking)}
            title={showThinking ? "Collapse details" : "Expand details"}
          >
            <div className="toggle-icon">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                <path 
                  d="M3 4.5L6 7.5L9 4.5" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  className={`chevron ${showThinking ? 'rotated' : ''}`}
                />
              </svg>
            </div>
          </button>
        </div>

        {/* Expandable Details */}
        {showThinking && (
          <div className="thinking-details">
            {/* Show refined query when available */}
            {refinedQuery && (
              <div className="refined-query-compact">
                <span className="query-label-compact">Query:</span>
                <span className="query-text-compact glazed-text">{refinedQuery}</span>
              </div>
            )}

            {/* Unified Steps Container */}
            <div className="steps-container-compact">
              {displaySteps.map((step, index) => (
                <div 
                  key={`${step.step_number}-${step.type}-${step.timestamp}`}
                  className={`step-compact ${step.type} ${index === displaySteps.length - 1 && isStreaming ? 'current' : 'completed'}`}
                >
                  <div className="step-content-compact">
                    {/* Skip step description since we've removed headings */}
                    
                    {/* Show reasoning for thought steps */}
                    {step.type === 'thought' && step.content?.details?.reasoning && (
                      <div className="step-detail-compact">
                        <span className="glazed-text">{step.content.details.reasoning}</span>
                      </div>
                    )}
                    
                    {/* Show content analysis for observation steps */}
                    {step.type === 'observation' && step.content?.details?.content_analysis && (
                      <div className="step-detail-compact">
                        <span className="glazed-text">{step.content.details.content_analysis}</span>
                      </div>
                    )}

                    {/* Show retrieval stats */}
                    {(step.content?.details?.output_length || step.content?.details?.retrieved_docs) && (
                      <div className="step-stats-compact">
                        {step.content.details.retrieved_docs && `${step.content.details.retrieved_docs} documents`}
                        {step.content.details.output_length && step.content.details.retrieved_docs && ' • '}
                        {step.content.details.output_length && `${Math.round(step.content.details.output_length / 100) / 10}K characters`}
                      </div>
                    )}

                    {/* Show error if any */}
                    {step.content?.details?.error && (
                      <div className="step-error-compact">
                        {step.content.details.error}
                      </div>
                    )}

                    {/* Timestamp */}
                    <div className="step-timestamp-compact">
                      {formatTimestamp(step.timestamp)}
                    </div>
                  </div>
                </div>
              ))}

              {isStreaming && displaySteps.length === 0 && (
                <div className="step-compact loading">
                  <div className="step-content-compact">
                    {/* Simplified connecting message */}
                    <div className="step-detail-compact">
                      <span className="glazed-text">Understanding the context and key information requirements of the query before retrieving relevant documents....</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Enhanced Compact Styles with Glazing Effects */}
      <style jsx>{`
        .reasoning-container-compact {
          background: var(--message-ai-bg);
          border: 1px solid var(--border-color);
          border-radius: 12px;
          padding: 5px;
          margin: 3px 0;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          max-width: 680px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }

        .reasoning-container-compact::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 1px;
          background: linear-gradient(90deg, 
            transparent 0%, 
            rgba(74, 144, 226, 0.3) 20%, 
            rgba(74, 144, 226, 0.6) 50%, 
            rgba(74, 144, 226, 0.3) 80%, 
            transparent 100%
          );
        }

        .reasoning-header-compact {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 8px;
        }

        .status-display {
          flex: 1;
          display: flex;
          align-items: center;
        }

        .current-status {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 15px;
          color: var(--text-primary);
        }

        .cuckoo-status {
          font-weight: 600;
          color: var(--accent-color);
        }

        .status-text {
          font-weight: 600;
        }

        /* Enhanced Glazed Text Effect */
        .glazed-text {
          background: linear-gradient(135deg, 
            var(--text-primary) 0%,
            rgba(74, 144, 226, 0.9) 25%,
            var(--text-primary) 50%,
            rgba(74, 144, 226, 0.8) 75%,
            var(--text-primary) 100%
          );
          background-size: 200% 200%;
          background-clip: text;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          animation: glazeShimmer 3s ease-in-out infinite;
          position: relative;
        }

        .glazed-text::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, 
            transparent 0%, 
            rgba(255, 255, 255, 0.2) 50%, 
            transparent 100%
          );
          animation: glazeHighlight 2.5s ease-in-out infinite;
        }

        @keyframes glazeShimmer {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        @keyframes glazeHighlight {
          0% {
            left: -100%;
          }
          50% {
            left: 100%;
          }
          100% {
            left: -100%;
          }
        }

        /* Enhanced Expand Toggle - Simple without border */
        .expand-toggle {
          width: 32px;
          height: 32px;
          border: none;
          background: transparent;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          color: var(--text-secondary);
          position: relative;
        }

        .expand-toggle:hover {
          background: rgba(74, 144, 226, 0.1);
          color: var(--accent-color);
          transform: translateY(-1px);
        }

        .expand-toggle:active {
          transform: translateY(0);
        }

        .toggle-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 100%;
          height: 100%;
        }

        .chevron {
          transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          transform-origin: center;
        }

        .chevron.rotated {
          transform: rotate(180deg);
        }

        .thinking-details {
          animation: slideDown 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border-top: 1px solid var(--border-color);
          padding-top: 9px;
          background: linear-gradient(180deg, 
            rgba(74, 144, 226, 0.02) 0%, 
            transparent 100%
          );
          border-radius: 0 0 8px 8px;
          margin: 12px -16px -16px -16px;
          padding-left: 16px;
          padding-right: 16px;
          padding-bottom: 16px;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
            max-height: 0;
          }
          to {
            opacity: 1;
            transform: translateY(0);
            max-height: 1000px;
          }
        }

        .refined-query-compact {
          display: flex;
          align-items: center;
          gap: 4px;
          margin-bottom: 9px;
          padding: 8px;
          background: linear-gradient(135deg, 
            var(--bg-secondary) 0%, 
            rgba(74, 144, 226, 0.05) 100%
          );
          border-radius: 8px;
          font-size: 13px;
          border: 1px solid rgba(74, 144, 226, 0.1);
        }

        .query-label-compact {
          color: var(--text-secondary);
          font-weight: 600;
          min-width: 40px;
        }

        .query-text-compact {
          color: var(--text-primary);
          font-family: 'SF Mono', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          font-size: 12px;
          flex: 1;
          font-weight: 500;
        }

        .steps-container-compact {
          background: linear-gradient(135deg, 
            var(--bg-secondary) 0%, 
            rgba(74, 144, 226, 0.03) 100%
          );
          border-radius: 8px;
          padding: 8px;
          border: 1px solid rgba(74, 144, 226, 0.1);
        }

        .step-compact {
          padding: 8px 0;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
        }

        .step-compact:not(:last-child) {
          border-bottom: 1px solid rgba(74, 144, 226, 0.1);
          margin-bottom: 4px;
        }

        .step-compact.current {
          background: linear-gradient(135deg, 
            rgba(74, 144, 226, 0.08) 0%, 
            rgba(74, 144, 226, 0.03) 100%
          );
          margin: -8px -8px;
          padding: 9px 4px;
          border-radius: 6px;
          border: 1px solid rgba(74, 144, 226, 0.2);
          box-shadow: 0 2px 8px rgba(74, 144, 226, 0.1);
        }

        .step-content-compact {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .step-detail-compact {
          font-size: 14px;
          color: var(--text-secondary);
          line-height: 1.5;
          padding-left: 9px;
          border-left: 3px solid rgba(74, 144, 226, 0.3);
          position: relative;
        }

        .step-detail-compact::before {
          content: '';
          position: absolute;
          left: -3px;
          top: 0;
          bottom: 0;
          width: 3px;
          background: linear-gradient(180deg, 
            rgba(74, 144, 226, 0.6) 0%,
            rgba(74, 144, 226, 0.2) 100%
          );
        }

        .step-stats-compact {
          font-size: 13px;
          background: linear-gradient(135deg, 
            rgba(39, 174, 96, 0.1) 0%,
            rgba(39, 174, 96, 0.05) 100%
          );
          color: var(--positive-feedback);
          padding: 4px 8px;
          border-radius: 6px;
          display: inline-block;
          font-weight: 600;
          border: 1px solid rgba(39, 174, 96, 0.2);
        }

        .step-error-compact {
          font-size: 13px;
          background: linear-gradient(135deg, 
            rgba(231, 76, 60, 0.1) 0%,
            rgba(231, 76, 60, 0.05) 100%
          );
          color: var(--negative-feedback);
          padding: 6px 8px;
          border-radius: 6px;
          font-family: 'SF Mono', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          border: 1px solid rgba(231, 76, 60, 0.2);
        }

        .step-timestamp-compact {
          font-size: 11px;
          color: var(--text-secondary);
          font-family: 'SF Mono', 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
          opacity: 0.8;
          font-weight: 500;
        }

        .step-compact.loading .step-description-compact {
          opacity: 0.7;
        }

        .analysis-complete-indicator {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          margin-top: 12px;
          background: linear-gradient(135deg, 
            rgba(40, 167, 69, 0.1) 0%,
            rgba(40, 167, 69, 0.05) 100%
          );
          border: 1px solid rgba(40, 167, 69, 0.2);
          border-radius: 8px;
          font-size: 13px;
          color: var(--positive-feedback, #28a745);
          font-weight: 600;
        }
        
        .complete-icon {
          width: 18px;
          height: 18px;
          background: var(--positive-feedback, #28a745);
          color: white;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 11px;
          font-weight: bold;
          box-shadow: 0 2px 4px rgba(40, 167, 69, 0.3);
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .reasoning-container-compact {
            padding: 12px;
            margin: 6px 0;
          }
          
          .current-status {
            font-size: 14px;
          }
          
          .step-detail-compact {
            font-size: 13px;
          }
          
          .refined-query-compact {
            flex-direction: column;
            align-items: flex-start;
            gap: 6px;
          }

          .expand-toggle {
            width: 28px;
            height: 28px;
          }
        }

        /* Dark mode adjustments */
        @media (prefers-color-scheme: dark) {
          .glazed-text {
            background: linear-gradient(135deg, 
              var(--text-primary) 0%,
              rgba(100, 170, 255, 0.9) 25%,
              var(--text-primary) 50%,
              rgba(100, 170, 255, 0.8) 75%,
              var(--text-primary) 100%
            );
            background-size: 200% 200%;
            background-clip: text;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }
        }
      `}</style>
    </div>
  );
}

export default StreamingReasoningChain;